'use client';

import { useState, useRef, FormEvent, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import MobileHeader from '../components/MobileHeader';
import PhoneCountrySelector from '../components/PhoneCountrySelector';

interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

const defaultCountry: Country = {
  code: 'US',
  name: 'Estados Unidos',
  dialCode: '+1',
  flag: '🇺🇸',
};

export default function Contact() {
  const router = useRouter();
  const [wordCount, setWordCount] = useState(0);
  const [isValid, setIsValid] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<Country>(defaultCountry);
  const mensajeRef = useRef<HTMLTextAreaElement>(null);
  const telefonoRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Redirigir al home después de 5 segundos cuando el envío sea exitoso
  useEffect(() => {
    if (submitStatus === 'success') {
      const redirectTimer = setTimeout(() => {
        router.push('/');
      }, 5000);

      // Limpiar el timer si el componente se desmonta o el estado cambia
      return () => clearTimeout(redirectTimer);
    }
  }, [submitStatus, router]);

  // Actualizar el placeholder del teléfono cuando cambie el país (sin el indicativo)
  useEffect(() => {
    if (telefonoRef.current) {
      telefonoRef.current.placeholder = '(555) 123-4567';
    }
  }, [selectedCountry]);

  // Header HTML extraído del home
  const headerHTML = `<div class="w-full fixed top-0 p-3 flex justify-center items-center z-20 flex-col max-sm:hidden"><div class="w-full max-w-4xl flex justify-between items-center bg-[#f4f4f4] bg-opacity-40 rounded-full backdrop-blur-md px-2.5 py-2"><a aria-current="page" class="router-link-active router-link-exact-active flex gap-1 items-center" href="/"><div class="w-[36px] h-[36px]"><img src="/AbbiAI.png" alt="AbbiAI Logo" class="h-full w-full object-contain"/></div><div class="flex flex-col justify-center items-center"><h2 class="text-[18px] font-bold text-darkbrown dark:text-white"> AbbiAI</h2><span class="italic text-xs">IA con Propósito</span></div></a><nav class="hidden md:flex gap-5 items-center text-darkbrown"><a class="hover:underline" href="/">Inicio</a><a class="hover:underline" href="/pricing">Precios</a><a aria-current="page" class="router-link-active router-link-exact-active hover:underline" href="/contact">Contáctanos</a></nav><div class="flex gap-5 items-center text-darkbrown"><a class="" href="https://app.abbiai.it/" target="_blank" rel="noopener noreferrer">Iniciar App</a></div></div></div>`;

  // Footer HTML extraído del home
  const footerHTML = `<div class="w-full flex flex-col bg-accentdarker border-t border-gray-300 py-7 gap-10 items-center"><div class="w-full max-w-5xl flex items-start justify-between px-8 max-md:flex-col max-md:gap-8"><div class="flex flex-col items-center gap-2.5"><div class="flex gap-1 items-center"><div class="w-[36px] h-[36px]"><img src="/AbbiAI.png" alt="AbbiAI Logo" class="h-full w-full object-contain"/></div><div class="flex flex-col justify-center items-start"><h2 class="text-[18px] font-bold text-darkbrown dark:text-white"> AbbiAI</h2><span class="italic text-xs">IA con Propósito</span></div></div><div class="flex items-center gap-3"><a onclick="return false;" style="cursor: pointer;"><div class="h-[32px]"><img alt="LinkedIn" class="h-full" src="data:image/svg+xml,%3c?xml%20version='1.0'%20encoding='utf-8'?%3e%3c!--%20Uploaded%20to:%20SVG%20Repo,%20www.svgrepo.com,%20Generator:%20SVG%20Repo%20Mixer%20Tools%20--%3e%3csvg%20width='800px'%20height='800px'%20viewBox='0%200%2024%2024'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3cpath%20d='M18.72%203.99997H5.37C5.19793%203.99191%205.02595%204.01786%204.86392%204.07635C4.70189%204.13484%204.55299%204.22471%204.42573%204.34081C4.29848%204.45692%204.19537%204.59699%204.12232%204.75299C4.04927%204.909%204.0077%205.07788%204%205.24997V18.63C4.01008%2018.9901%204.15766%2019.3328%204.41243%2019.5875C4.6672%2019.8423%205.00984%2019.9899%205.37%2020H18.72C19.0701%2019.9844%2019.4002%2019.8322%2019.6395%2019.5761C19.8788%2019.32%2020.0082%2018.9804%2020%2018.63V5.24997C20.0029%205.08247%2019.9715%204.91616%2019.9078%204.76122C19.8441%204.60629%2019.7494%204.466%2019.6295%204.34895C19.5097%204.23191%2019.3672%204.14059%2019.2108%204.08058C19.0544%204.02057%2018.8874%203.99314%2018.72%203.99997ZM9%2017.34H6.67V10.21H9V17.34ZM7.89%209.12997C7.72741%209.13564%207.5654%209.10762%207.41416%209.04768C7.26291%208.98774%207.12569%208.89717%207.01113%208.78166C6.89656%208.66615%206.80711%208.5282%206.74841%208.37647C6.6897%208.22474%206.66301%208.06251%206.67%207.89997C6.66281%207.73567%206.69004%207.57169%206.74995%207.41854C6.80986%207.26538%206.90112%207.12644%207.01787%207.01063C7.13463%206.89481%207.2743%206.80468%207.42793%206.74602C7.58157%206.68735%207.74577%206.66145%207.91%206.66997C8.07259%206.66431%208.2346%206.69232%208.38584%206.75226C8.53709%206.8122%208.67431%206.90277%208.78887%207.01828C8.90344%207.13379%208.99289%207.27174%209.05159%207.42347C9.1103%207.5752%209.13699%207.73743%209.13%207.89997C9.13719%208.06427%209.10996%208.22825%209.05005%208.3814C8.99014%208.53456%208.89888%208.6735%208.78213%208.78931C8.66537%208.90513%208.5257%208.99526%208.37207%209.05392C8.21843%209.11259%208.05423%209.13849%207.89%209.12997ZM17.34%2017.34H15V13.44C15%2012.51%2014.67%2011.87%2013.84%2011.87C13.5822%2011.8722%2013.3313%2011.9541%2013.1219%2012.1045C12.9124%2012.2549%2012.7546%2012.4664%2012.67%2012.71C12.605%2012.8926%2012.5778%2013.0865%2012.59%2013.28V17.34H10.29V10.21H12.59V11.21C12.7945%2010.8343%2013.0988%2010.5225%2013.4694%2010.3089C13.84%2010.0954%2014.2624%209.98848%2014.69%209.99997C16.2%209.99997%2017.34%2011%2017.34%2013.13V17.34Z'%20fill='%23000000'/%3e%3c/svg%3e" width="25"/></div></a><a onclick="return false;" style="cursor: pointer;"><div class="h-[25px]"><img alt="Twitter" class="h-full" src="data:image/svg+xml,%3c?xml%20version='1.0'%20encoding='iso-8859-1'?%3e%3c!DOCTYPE%20svg%20PUBLIC%20'-//W3C//DTD%20SVG%201.1//EN'%20'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3e%3c!--%20Uploaded%20to:%20SVG%20Repo,%20www.svgrepo.com,%20Generator:%20SVG%20Repo%20Mixer%20Tools%20--%3e%3csvg%20fill='%23000000'%20version='1.1'%20xmlns='http://www.w3.org/2000/svg'%20xmlns:xlink='http://www.w3.org/1999/xlink'%20width='800px'%20height='800px'%20viewBox='0%200%20512%20512'%20xml:space='preserve'%3e%3cg%20id='7935ec95c421cee6d86eb22ecd12f847'%3e%3cpath%20style='display:%20inline;'%20d='M459.186,151.787c0.203,4.501,0.305,9.023,0.305,13.565%20c0,138.542-105.461,298.285-298.274,298.285c-59.209,0-114.322-17.357-160.716-47.104c8.212,0.973,16.546,1.47,25.012,1.47%20c49.121,0,94.318-16.759,130.209-44.884c-45.887-0.841-84.596-31.154-97.938-72.804c6.408,1.227,12.968,1.886,19.73,1.886%20c9.55,0,18.816-1.287,27.617-3.68c-47.955-9.633-84.1-52.001-84.1-102.795c0-0.446,0-0.882,0.011-1.318%20c14.133,7.847,30.294,12.562,47.488,13.109c-28.134-18.796-46.637-50.885-46.637-87.262c0-19.212,5.16-37.218,14.193-52.7%20c51.707,63.426,128.941,105.156,216.072,109.536c-1.784-7.675-2.718-15.674-2.718-23.896c0-57.891,46.941-104.832,104.832-104.832%20c30.173,0,57.404,12.734,76.525,33.102c23.887-4.694,46.313-13.423,66.569-25.438c-7.827,24.485-24.434,45.025-46.089,58.002%20c21.209-2.535,41.426-8.171,60.222-16.505C497.448,118.542,479.666,137.004,459.186,151.787z'%3e%3c/path%3e%3c/g%3e%3c/svg%3e" width="20"/></div></a><a onclick="return false;" style="cursor: pointer;"><div class="h-[30px]"><img alt="Youtube" class="h-full" src="data:image/svg+xml,%3c?xml%20version='1.0'%20encoding='utf-8'?%3e%3c!--%20Uploaded%20to:%20SVG%20Repo,%20www.svgrepo.com,%20Generator:%20SVG%20Repo%20Mixer%20Tools%20--%3e%3csvg%20fill='%23000000'%20width='800px'%20height='800px'%20viewBox='0%200%2032%2032'%20version='1.1'%20xmlns='http://www.w3.org/2000/svg'%3e%3ctitle%3eyoutube%3c/title%3e%3cpath%20d='M12.932%2020.459v-8.917l7.839%204.459zM30.368%208.735c-0.354-1.301-1.354-2.307-2.625-2.663l-0.027-0.006c-3.193-0.406-6.886-0.638-10.634-0.638-0.381%200-0.761%200.002-1.14%200.007l0.058-0.001c-0.322-0.004-0.701-0.007-1.082-0.007-3.748%200-7.443%200.232-11.070%200.681l0.434-0.044c-1.297%200.363-2.297%201.368-2.644%202.643l-0.006%200.026c-0.4%202.109-0.628%204.536-0.628%207.016%200%200.088%200%200.176%200.001%200.263l-0-0.014c-0%200.074-0.001%200.162-0.001%200.25%200%202.48%200.229%204.906%200.666%207.259l-0.038-0.244c0.354%201.301%201.354%202.307%202.625%202.663l0.027%200.006c3.193%200.406%206.886%200.638%2010.634%200.638%200.38%200%200.76-0.002%201.14-0.007l-0.058%200.001c0.322%200.004%200.702%200.007%201.082%200.007%203.749%200%207.443-0.232%2011.070-0.681l-0.434%200.044c1.298-0.362%202.298-1.368%202.646-2.643l0.006-0.026c0.399-2.109%200.627-4.536%200.627-7.015%200-0.088-0-0.176-0.001-0.263l0%200.013c0-0.074%200.001-0.162%200.001-0.25%200-2.48-0.229-4.906-0.666-7.259l0.038%200.244z'%3e%3c/path%3e%3c/svg%3e" width="20"/></div></a><a onclick="return false;" style="cursor: pointer;"><div class="h-[30px]"><img alt="Discord" class="h-full" src="data:image/svg+xml,%3csvg%20width='30.67'%20xmlns='http://www.w3.org/2000/svg'%20height='23.249'%20viewBox='4019%20-1152%2030.67%2023.249'%20fill='none'%3e%3cpath%20d='M4045.001-1150.047a25.26%2025.26%200%200%200-6.339-1.953c-.279.48-.589%201.131-.806%201.643a24.228%2024.228%200%200%200-7.022%200%2020.164%2020.164%200%200%200-.806-1.643%2024.911%2024.911%200%200%200-6.341%201.953c-4.014%205.921-5.099%2011.702-4.557%2017.406%202.666%201.937%205.239%203.115%207.767%203.89a18.866%2018.866%200%200%200%201.658-2.681%2016.368%2016.368%200%200%201-2.619-1.256%209.64%209.64%200%200%200%20.635-.496c5.053%202.31%2010.526%202.31%2015.517%200%20.217.171.418.341.635.496-.837.496-1.705.914-2.619%201.256.48.945%201.038%201.844%201.658%202.681a25.708%2025.708%200%200%200%207.767-3.89c.666-6.603-1.056-12.338-4.528-17.406Zm-15.748%2013.887c-1.519%200-2.759-1.379-2.759-3.069%200-1.689%201.209-3.068%202.759-3.068%201.534%200%202.789%201.379%202.758%203.068%200%201.69-1.224%203.069-2.758%203.069Zm10.184%200c-1.519%200-2.76-1.379-2.76-3.069%200-1.689%201.21-3.068%202.76-3.068%201.535%200%202.79%201.379%202.759%203.068%200%201.69-1.209%203.069-2.759%203.069Z'%20style='fill:%20rgb(0,%200,%200);%20fill-opacity:%201;'%20class='fills'%20data-testid='svg-path'/%3e%3c/svg%3e" width="20"/></div></a></div></div><div class="flex flex-col gap-2.5"><div class="font-bold"><a href="https://abbiai.it" rel="noopener noreferrer" target="_blank">Más Información</a></div><div class="flex flex-col gap-2"><a class="hover:underline" onclick="return false;" style="cursor: pointer;">Descarga la App en cualquier dispositivo</a><a class="hover:underline" onclick="return false;" style="cursor: pointer;">Herramientas Avanzadas con IA</a><a class="hover:underline" onclick="return false;" style="cursor: pointer;">Obtén siempre los mejores resultados</a></div></div><div class="flex flex-col gap-2.5"><div class="font-bold">Quiénes Somos</div><div class="flex flex-col gap-2"><a class="hover:underline" onclick="return false;" style="cursor: pointer;">Quiénes Somos</a><a class="hover:underline" onclick="return false;" style="cursor: pointer;">Política de Privacidad</a><a class="hover:underline" onclick="return false;" style="cursor: pointer;">Términos de Servicio</a></div></div></div><div class="flex flex-col gap-2 items-center text-center"><p>© 2025 AbbiAI. All rights reserved.</p></div></div>`;

  const handleWordCount = (text: string) => {
    const words = text.trim().split(/\s+/).filter((word) => word.length > 0);
    const count = words.length;
    setWordCount(count);
    setIsValid(count <= 50 && count > 0);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!isValid || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const formData = new FormData(e.currentTarget);
      const phoneNumber = formData.get('telefono') as string;
      
      // Validar formato del teléfono (números, espacios, paréntesis, guiones)
      const phoneRegex = /^[0-9\s()\-]+$/;
      if (!phoneRegex.test(phoneNumber)) {
        setSubmitStatus('error');
        setErrorMessage('El número telefónico contiene caracteres no válidos. Solo se permiten números, espacios, paréntesis y guiones.');
        setIsSubmitting(false);
        return;
      }
      // Combinar el indicativo del país con el número telefónico
      const fullPhoneNumber = `${selectedCountry.dialCode} ${phoneNumber}`.trim();
      
      const data = {
        nombre: formData.get('nombre') as string,
        email: formData.get('email') as string,
        telefono: fullPhoneNumber,
        mensaje: formData.get('mensaje') as string,
      };

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        formRef.current?.reset();
        setWordCount(0);
        setIsValid(true);
        
        // La redirección al home se maneja en el useEffect
        // después de 5 segundos
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.error || 'Error al enviar el mensaje');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Error al enviar el mensaje. Por favor, intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <MobileHeader />
      <div className="pt-20 md:pt-0">
        <div dangerouslySetInnerHTML={{ __html: headerHTML }} />
      </div>
      
      <div className="w-full min-h-screen bg-[#f9fff6] py-12 md:py-20 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10">
            <h1 className="text-3xl md:text-4xl font-bold text-darkbrown mb-2 text-center">
              Contáctanos
            </h1>
            <p className="text-gray-600 text-center mb-8 md:mb-10">
              Estamos aquí para ayudarte. Envíanos un mensaje y te responderemos lo antes posible.
            </p>

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              {/* Nombre Completo */}
              <div className="flex flex-col gap-2">
                <label htmlFor="nombre" className="text-sm md:text-base font-medium text-darkbrown">
                  Nombre Completo
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-lightbrown focus:border-transparent text-base"
                  placeholder="Ingresa tu nombre completo"
                  title="El nombre completo es obligatorio"
                  onInvalid={(e) => {
                    const target = e.target as HTMLInputElement;
                    if (target.validity.valueMissing) {
                      target.setCustomValidity('El nombre completo es obligatorio');
                    }
                  }}
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;
                    target.setCustomValidity('');
                  }}
                />
              </div>

              {/* Correo Empresarial y Número Telefónico en la misma línea */}
              <div className="flex flex-col sm:flex-row gap-6">
                {/* Correo Empresarial */}
                <div className="flex flex-col gap-2 flex-1">
                  <label htmlFor="email" className="text-sm md:text-base font-medium text-darkbrown">
                    Correo Empresarial
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-lightbrown focus:border-transparent text-base"
                    placeholder="ejemplo@empresa.com"
                    title="Por favor ingresa un correo electrónico válido. Debe incluir un símbolo @ y un dominio válido."
                    onInvalid={(e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.validity.valueMissing) {
                        target.setCustomValidity('El correo electrónico es obligatorio');
                      } else if (target.validity.typeMismatch) {
                        target.setCustomValidity('Por favor incluye un símbolo @ en la dirección de correo');
                      } else if (target.validity.patternMismatch) {
                        target.setCustomValidity('El formato del correo electrónico no es válido');
                      }
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.setCustomValidity('');
                    }}
                  />
                </div>

                {/* Número Telefónico */}
                <div className="flex flex-col gap-2 flex-1">
                  <label htmlFor="telefono" className="text-sm md:text-base font-medium text-darkbrown">
                    Número Telefónico
                  </label>
                  <div className="flex">
                    <PhoneCountrySelector
                      selectedCountry={selectedCountry}
                      onCountryChange={setSelectedCountry}
                    />
                    <input
                      ref={telefonoRef}
                      type="tel"
                      id="telefono"
                      name="telefono"
                      required
                      minLength={7}
                      maxLength={20}
                      className="flex-1 px-4 py-3 border border-gray-300 border-l-0 rounded-r-lg rounded-l-none focus:outline-none focus:ring-2 focus:ring-lightbrown focus:border-transparent text-base"
                      placeholder="(555) 123-4567"
                      title="Por favor ingresa un número telefónico válido. Debe contener entre 7 y 20 caracteres y solo puede incluir números, espacios, paréntesis, guiones y el símbolo +."
                      onInvalid={(e) => {
                        const target = e.target as HTMLInputElement;
                        if (target.validity.valueMissing) {
                          target.setCustomValidity('El número telefónico es obligatorio');
                        } else if (target.validity.tooShort) {
                          target.setCustomValidity('El número telefónico debe tener al menos 7 caracteres');
                        } else if (target.validity.tooLong) {
                          target.setCustomValidity('El número telefónico no puede exceder 20 caracteres');
                        }
                      }}
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        const value = target.value;
                        // Validar que solo contenga números, espacios, paréntesis y guiones
                        const phoneRegex = /^[0-9\s()\-]*$/;
                        if (value.length > 0 && !phoneRegex.test(value)) {
                          target.setCustomValidity('Solo se permiten números, espacios, paréntesis y guiones');
                        } else if (value.length > 0 && value.length < 7) {
                          target.setCustomValidity('El número telefónico debe tener al menos 7 caracteres');
                        } else if (value.length > 20) {
                          target.setCustomValidity('El número telefónico no puede exceder 20 caracteres');
                        } else {
                          target.setCustomValidity('');
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Mensaje */}
              <div className="flex flex-col gap-2">
                <label htmlFor="mensaje" className="text-sm md:text-base font-medium text-darkbrown">
                  Mensaje
                </label>
                <textarea
                  ref={mensajeRef}
                  id="mensaje"
                  name="mensaje"
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-lightbrown focus:border-transparent text-base resize-none"
                  placeholder="Escribe tu mensaje aquí (máximo 50 palabras)..."
                  onChange={(e) => handleWordCount(e.target.value)}
                />
                <div className="flex justify-between items-center text-sm">
                  <span className={`${isValid ? 'text-gray-600' : 'text-red-500'}`}>
                    {wordCount} / 50 palabras
                  </span>
                  {!isValid && wordCount > 50 && (
                    <span className="text-red-500 text-xs">
                      El mensaje no puede exceder 50 palabras
                    </span>
                  )}
                </div>
              </div>

              {/* Mensaje de éxito/error */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-green-800">
                  <p className="font-semibold">¡Mensaje enviado correctamente!</p>
                  <p className="text-sm mt-1">
                    Te redirigiremos al inicio en unos momentos...
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-red-800">
                  <p className="font-semibold">Error al enviar el mensaje</p>
                  <p className="text-sm mt-1">{errorMessage}</p>
                </div>
              )}

              {/* Botón de envío */}
              <button
                type="submit"
                disabled={!isValid || isSubmitting}
                className={`w-full bg-lightbrown text-white rounded-lg px-6 py-4 font-bold text-lg md:text-xl transition-all min-h-[56px] flex items-center justify-center gap-3 ${
                  !isValid || isSubmitting
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-brown active:bg-darkbrown2 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Enviando...</span>
                  </>
                ) : (
                  <span>Enviar Mensaje</span>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div dangerouslySetInnerHTML={{ __html: footerHTML }} />
    </>
  );
}
